import React, { useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { IconUserCheck, IconChevronRight } from '@tabler/icons-react'
import { useAuth0 } from '@auth0/auth0-react'
import { useAppSelector } from '../store'
import { selectEater } from '../store/profileSlice'
import { ROUTES } from '../helpers/routes-helper'
import useAppUtilities from '../hooks/useAppUtilities'
import { logEvent } from '../analytics/analytics'
import { Eater } from '../types'

const DEFAULT_FIRST_NAME = 'John'
const DEFAULT_LAST_NAME = 'Smith'

function isProfileIncomplete(eater: Eater | undefined): boolean {
  if (!eater) return false

  const isDefaultName =
    eater.firstName === DEFAULT_FIRST_NAME && eater.lastName === DEFAULT_LAST_NAME

  // The banner is about "get better recommendations", so gate only on the recommendation-relevant
  // fields that onboarding actually captures: a real name (not the "John Smith" default) plus the
  // three taste signals. Other profile fields (phone/address/occupation/income/gender/ingredient*/
  // foodPreparation*) are intentionally excluded — onboarding never collects them, so requiring
  // them would keep the banner permanently visible.
  return (
    !eater.firstName ||
    !eater.lastName ||
    isDefaultName ||
    !eater.cuisineLikes ||
    !eater.dietaryPreferences ||
    !eater.allergies
  )
}

const ProfileCompletionBanner: React.FC = () => {
  const { isAuthenticated } = useAuth0()
  const { navigate, theme } = useAppUtilities()
  const eater = useAppSelector(selectEater)

  const shouldShow = isAuthenticated && isProfileIncomplete(eater)

  useEffect(() => {
    if (shouldShow) {
      logEvent('profile_completion_banner_viewed', { screen_name: 'home' })
    }
  }, [shouldShow])

  if (!shouldShow) {
    return null
  }

  const handleClick = (): void => {
    logEvent('profile_completion_clicked', { screen_name: 'home' })
    navigate(ROUTES.PROFILE)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Complete your profile to get better recommendations"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        paddingLeft: '12px',
        marginTop: '16px',
        marginBottom: '8px',
        borderRadius: '12px',
        border: `1px solid ${theme.palette.grey[200]}`,
        borderLeft: `4px solid ${theme.palette.warning.main}`,
        backgroundColor: theme.palette.common.white,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: theme.palette.grey[300],
          filter: 'drop-shadow(0px 2px 4px 0px #6666664D)',
        },
        '&:active': {
          backgroundColor: theme.palette.grey[50],
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: theme.palette.warning.main,
          outlineOffset: '2px',
        },
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          minWidth: '36px',
          borderRadius: '8px',
          backgroundColor: theme.palette.warning.light,
          marginRight: '12px',
        }}
      >
        <IconUserCheck size={20} stroke={2} color={theme.palette.warning.dark} />
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.grey[900],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Get better recommendations
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.grey[600],
            marginTop: '2px',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Complete your profile to personalize your results.
        </Typography>
      </Box>

      {/* CTA */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginLeft: '12px',
          flexShrink: 0,
          color: theme.palette.warning.dark,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: theme.palette.warning.dark,
            display: { xs: 'none', sm: 'block' },
          }}
        >
          Complete profile
        </Typography>
        <IconChevronRight size={20} stroke={2} color={theme.palette.warning.dark} />
      </Box>
    </Box>
  )
}

export default ProfileCompletionBanner
